import {
  PauseCircle,
  PlayCircleOutlineOutlined,
  Refresh,
} from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { FC, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { FramerTooltip } from "@ryde/controls";
import {
  liveMapPollingUiAtom,
  TOOLTIP_DELAY_BUTTONS,
  useFormats,
  useIsMobile,
} from "@ryde/shared";

import { LiveMapContext } from "../../context";
import { LiveMapGridStatusBreakdown } from "./live-map-icon-toggles";
import { LiveMapRideFilterDropdown } from "./live-map-ride-filter";
import { LiveMapDiagnosticsControl } from "./live-map.diagnostics.control";
import { LiveMapNewIncidentsToggle } from "./live-map.new-incidents-toggle";
import { LiveMapPollStatusPill } from "./live-map.poll-status-pill";
import { LiveMapRefreshIntervalControl } from "./live-map.refresh-interval.control";
import { LiveMapSelectAllCheckbox } from "./live-map.select-all-checkbox";
import {
  LiveMapPollingControlsGroupStyled,
  LiveMapPollTooltipLinesStyled,
  LiveMapTopControlsSelectGroupStyled,
  LiveMapTopControlsStyled,
} from "./live-map.styled";
import { useLiveMapToolbarStats } from "./use-live-map-toolbar-stats";

export const LiveMapTopControlsGrid: FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();
  const { formatDateTimeWithSecondsNormalized } = useFormats();
  const { gridStatusCounts } = useLiveMapToolbarStats();
  const { initGridPolling, stopGridPolling, refreshGridAndMapSelection } =
    useContext(LiveMapContext);
  const {
    isGridPollingRunning,
    lastSynchroGridDate,
    gridPoolingCount = 0,
    isGridPollFetchInFlight,
  } = useRecoilValue(liveMapPollingUiAtom);

  const onRefreshGridMapClick = useEvent(() => {
    refreshGridAndMapSelection?.();
  });

  const onPauseResumeClick = useEvent(() => {
    if (isGridPollingRunning) {
      stopGridPolling?.();
    } else {
      initGridPolling?.();
    }
  });

  const pauseResumeTooltip = useMemo(
    () =>
      isGridPollingRunning
        ? t("rideMap.PAUSE_GRID_UPDATE")
        : t("rideMap.RESUME_GRID_UPDATE"),
    [isGridPollingRunning, t],
  );

  const gridPollTooltip = useMemo(() => {
    if (!lastSynchroGridDate) {
      return t("rideMap.LIVE_MAP_GRID_POLL_STATUS_PENDING");
    }

    const lastTime = formatDateTimeWithSecondsNormalized(lastSynchroGridDate);

    return (
      <LiveMapPollTooltipLinesStyled>
        <span>
          {t("rideMap.LIVE_MAP_GRID_UPDATED_AT")} {lastTime}
        </span>
        <span>
          {t("rideMap.LIVE_MAP_DATA_LOADED_TIMES", { count: gridPoolingCount })}
        </span>
      </LiveMapPollTooltipLinesStyled>
    );
  }, [
    formatDateTimeWithSecondsNormalized,
    gridPoolingCount,
    lastSynchroGridDate,
    t,
  ]);

  return (
    <LiveMapTopControlsStyled>
      <div className="status_synchro">
        <LiveMapTopControlsSelectGroupStyled>
          <LiveMapSelectAllCheckbox />
        </LiveMapTopControlsSelectGroupStyled>
        <LiveMapRideFilterDropdown />
        {!isMobile && <LiveMapNewIncidentsToggle />}
        <FramerTooltip
          content={t("rideMap.FETCH_GRID_MAP_STATE")}
          mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}
        >
          <IconButton
            aria-label={t("rideMap.FETCH_GRID_MAP_STATE")}
            size="small"
            color="info"
            onClick={onRefreshGridMapClick}
          >
            <Refresh />
          </IconButton>
        </FramerTooltip>
        <LiveMapPollingControlsGroupStyled>
          <LiveMapRefreshIntervalControl
            variant="grid"
            hideSecInput={isMobile}
          />
          <FramerTooltip
            content={pauseResumeTooltip}
            mouseEnterDelay={TOOLTIP_DELAY_BUTTONS}
          >
            <IconButton size="small" onClick={onPauseResumeClick}>
              {isGridPollingRunning && <PauseCircle />}
              {!isGridPollingRunning && <PlayCircleOutlineOutlined />}
            </IconButton>
          </FramerTooltip>
        </LiveMapPollingControlsGroupStyled>
        {isMobile && <LiveMapDiagnosticsControl compactLabel />}
        {!isMobile && (
          <LiveMapPollStatusPill
            variant="inline"
            tooltip={gridPollTooltip}
            pollInfoAriaLabel={t("rideMap.LIVE_MAP_GRID_UPDATED_AT")}
            isInFlight={Boolean(isGridPollFetchInFlight)}
            isPollingActive={Boolean(isGridPollingRunning)}
            hasSyncedAtLeastOnce={Boolean(lastSynchroGridDate)}
          >
            <LiveMapGridStatusBreakdown statusCounts={gridStatusCounts} />
          </LiveMapPollStatusPill>
        )}
      </div>
    </LiveMapTopControlsStyled>
  );
};
